import { Box, Skeleton } from "@mui/material";
import { memo, useMemo } from "react";
import { buildShiftBands } from "../utils/calendarUtils";
import {
	buildDayRuns,
	DAY_GRID_COLUMNS,
	type DayLine,
	EXTRA_LANE_HEIGHT,
	GRID_BORDER,
	HOURS_PER_DAY,
	ROW_HEIGHT,
} from "../utils/dayHourGrid";
import { DayRowLabel } from "./dayRow/DayRowLabel";
import { EventRunsLayer } from "./dayRow/EventRunsLayer";
import { FullDayBlock } from "./dayRow/FullDayBlock";
import { HourCrosshair } from "./dayRow/HourCrosshair";
import { ShiftBandsLayer } from "./dayRow/ShiftBandsLayer";

type Props = DayLine & {
	dateKey: string;
	tz: string;
	/** highlights the shift containing now; null on the tomorrow board */
	nowMs: number | null;
	nowHour: number | null;
	/** column crosshair, shared with the board header */
	hoveredHour: number | null;
	onHourHover: (hour: number | null) => void;
	/** page-level so an event spanning midnight lights up on both boards */
	hoveredRunKey: string | null;
	onRunHover: (key: string | null) => void;
	onLineClick: (slug: string) => void;
};

const DayHourRowBase = ({
	assetName,
	slug,
	accentColor,
	events,
	dayShifts,
	isLoading,
	errorMessage,
	dateKey,
	tz,
	nowMs,
	nowHour,
	hoveredHour,
	onHourHover,
	hoveredRunKey,
	onRunHover,
	onLineClick,
}: Props) => {
	const bands = useMemo(
		() => buildShiftBands(dayShifts, tz).get(dateKey) ?? [],
		[dayShifts, dateKey, tz],
	);
	const { runs, laneCount, fullDayBlock } = useMemo(
		() => buildDayRuns(events, dateKey, tz),
		[events, dateKey, tz],
	);

	const height = ROW_HEIGHT + Math.max(0, laneCount - 1) * EXTRA_LANE_HEIGHT;
	const dimmed =
		!isLoading && !errorMessage && !fullDayBlock && runs.length === 0;

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: DAY_GRID_COLUMNS,
				backgroundColor: "#ffffff",
				"&:hover": { backgroundColor: "#EDEDED" },
			}}
		>
			<DayRowLabel
				assetName={assetName}
				accentColor={accentColor}
				errorMessage={errorMessage}
				height={height}
				dimmed={dimmed}
				onClick={() => onLineClick(slug)}
			/>

			{isLoading ? (
				<Box
					sx={{
						gridColumn: "2 / -1",
						minHeight: height,
						borderBottom: GRID_BORDER,
						p: "2px",
					}}
				>
					<Skeleton variant="rectangular" height="100%" />
				</Box>
			) : fullDayBlock ? (
				<FullDayBlock block={fullDayBlock} height={height} />
			) : (
				<Box
					onMouseMove={(e) => {
						const rect = e.currentTarget.getBoundingClientRect();
						const hour = Math.floor(
							((e.clientX - rect.left) / rect.width) * HOURS_PER_DAY,
						);
						onHourHover(Math.min(HOURS_PER_DAY - 1, Math.max(0, hour)));
					}}
					onMouseLeave={() => onHourHover(null)}
					sx={{
						gridColumn: "2 / -1",
						position: "relative",
						minHeight: height,
						borderBottom: GRID_BORDER,
						opacity: dimmed ? 0.75 : 1,
					}}
				>
					<HourCrosshair nowHour={nowHour} hoveredHour={hoveredHour} />
					<ShiftBandsLayer bands={bands} nowMs={nowMs} />
					<EventRunsLayer
						runs={runs}
						laneCount={laneCount}
						hoveredRunKey={hoveredRunKey}
						onRunHover={onRunHover}
					/>
				</Box>
			)}
		</Box>
	);
};

export const DayHourRow = memo(DayHourRowBase);
